import React, { FunctionComponent, useMemo, CSSProperties, useEffect, useRef } from "react";
import { SK_CHAT_HISTORY_TABLE_CLASS, DETAIL_TABLE_COLUMN_CLASSES, DETAIL_TABLE_COLUMN_HEADERS, ROWS_PER_PAGE_OPTIONS, INITIAL_LOAD_FAILURE_MESSAGE, INITIAL_LOAD_FAILURE_DESCRIPTION, UNAUTHORIZED_REDIRECT_MESSAGE, UNAUTHORIZED_REDIRECT_DESCRIPTION, NO_CHAT_SESSION_FOUND, SESSIONS_APPEAR_MESSAGE, INITIAL_LOAD_FAILURE_MESSAGE_SESSION_DETAIL } from "../../../constants/sidekick";
import { HeaderField, Table, TableBody, TableField, TableHeader, TableRow, UpIcon } from "@panviva/panviva-react-ui";
import { ChatDetailTableProps, ChatDetailMessage, AIContentDorcResult } from "../../../types/sidekick";
import { formatTimestamp, transformFeedback, formatCitations, formatDorcCitations, isAIContent, extractAIResponseContent } from "../../../utils/transformers/sidekickTransformers";
import { LoadingIndicator } from "../../common/LoadingIndicator";
import { NoResultsFound } from "../../common/NoResultsFound";
import "../historyView/ChatHistoryTable.scss";
import "./ChatDetailTable.scss";

// Style objects for text wrapping
const textWrapStyle: CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: '400px'
};

const citationsWrapStyle: CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: '200px'
};

/**
 * Delays (in milliseconds) for scroll position retry attempts.
 * Multiple attempts are necessary because the Panviva Table component
 * restores scroll position after initial render, so we need to force
 * the scroll position multiple times to ensure it sticks.
 */
const SCROLL_RETRY_DELAYS = [0, 50, 150, 300, 500];

// Props for message row components
interface MessageRowProps {
    item: ChatDetailMessage;
    sender: string;
    timestamp: string;
    citations: string;
}

// User message row component
const UserMessageRow: React.FC<MessageRowProps> = ({ item, sender, timestamp }) => {
    const content = typeof item.content === 'string' ? item.content : '';
    const feedback = transformFeedback(item.feedback);
    return (
        <TableRow
            role="row"
            tabIndex={0}
            aria-label={`Sender: ${sender}, Conversation: ${content}, Timestamp: ${timestamp}, Feedback: ${feedback}, Citations: NA`}
        >
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.SENDER} role="cell">{sender}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CONVERSATION} role="cell" style={textWrapStyle}>
                {content}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.TIMESTAMP} role="cell">{timestamp}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.FEEDBACK} role="cell">
                {feedback}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CITATIONS} role="cell" style={citationsWrapStyle}>NA</TableField>
        </TableRow>
    );
};

// Curated response row component
const CuratedResponseRow: React.FC<MessageRowProps & { dorcResult?: AIContentDorcResult }> = ({
    item, timestamp, citations, dorcResult
}) => {
    const content = dorcResult?.simple_content || '';
    const feedback = transformFeedback(item.feedback);
    const citationText = dorcResult ? formatDorcCitations(dorcResult) : citations;
    return (
        <TableRow
            role="row"
            tabIndex={0}
            aria-label={`Sender: Curated Response, Conversation: ${content}, Timestamp: ${timestamp}, Feedback: ${feedback}, Citations: ${citationText}`}
        >
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.SENDER} role="cell">Curated Response</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CONVERSATION} role="cell" style={textWrapStyle}>
                {content}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.TIMESTAMP} role="cell">{timestamp}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.FEEDBACK} role="cell">
                {feedback}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CITATIONS} role="cell" style={citationsWrapStyle}>
                {citationText}
            </TableField>
        </TableRow>
    );
};

// AI message row component
const AIMessageRow: React.FC<MessageRowProps & { content: string }> = ({
    item, timestamp, citations, content
}) => {
    const feedback = transformFeedback(item.feedback);
    return (
        <TableRow
            role="row"
            tabIndex={0}
            aria-label={`Sender: AI, Conversation: ${content}, Timestamp: ${timestamp}, Feedback: ${feedback}, Citations: ${citations}`}
        >
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.SENDER} role="cell">AI</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CONVERSATION} role="cell" style={textWrapStyle}>
                {content}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.TIMESTAMP} role="cell">{timestamp}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.FEEDBACK} role="cell">
                {feedback}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CITATIONS} role="cell" style={citationsWrapStyle}>{citations}</TableField>
        </TableRow>
    );
};

// Default message row component when no content is available
const DefaultMessageRow: React.FC<MessageRowProps> = ({ item, sender, timestamp }) => {
    const feedback = transformFeedback(item.feedback);
    return (
        <TableRow
            role="row"
            tabIndex={0}
            aria-label={`Sender: ${sender}, Conversation: No content available, Timestamp: ${timestamp}, Feedback: ${feedback}, Citations: NA`}
        >
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.SENDER} role="cell">{sender}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CONVERSATION} role="cell" style={textWrapStyle}>
                No content available
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.TIMESTAMP} role="cell">{timestamp}</TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.FEEDBACK} role="cell">
                {feedback}
            </TableField>
            <TableField className={DETAIL_TABLE_COLUMN_CLASSES.CITATIONS} role="cell" style={citationsWrapStyle}>NA</TableField>
        </TableRow>
    );
};

/**
 * Main ChatDetailTable component
 */
export const ChatDetailTable: FunctionComponent<ChatDetailTableProps & { isUnauthorizedInitialLoadFailure?: boolean }> = ({
    currentItems,
    currentPage,
    rowsPerPage,
    totalItems,
    totalPages,
    handleFirstPage,
    handlePrevPage,
    handleNextPage,
    handleLastPage,
    handleChangeRowsPerPage,
    isLoading,
    scrollKey,
    isInitialLoadFailure,
    isUnauthorizedInitialLoadFailure = false,
}) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const errorMessageRef = useRef<HTMLDivElement>(null);

    // Focus management for initial load failure errors
    useEffect(() => {
        if (isInitialLoadFailure && !isLoading && currentItems.length === 0) {
            // Move focus to error message for accessibility
            // Use requestAnimationFrame for more reliable timing across different devices
            let timerId: number;
            requestAnimationFrame(() => {
                timerId = window.setTimeout(() => {
                    errorMessageRef.current?.focus();
                }, 150);
            });
            return () => {
                if (timerId) {
                    clearTimeout(timerId);
                }
            };
        }
    }, [isInitialLoadFailure, isLoading, currentItems.length]);

    // Scroll to top whenever scrollKey increments (page changed).
    // scrollKey is never incremented on rows-per-page change, so scroll is preserved then.
    useEffect(() => {
        if (!scrollKey) return; // skip initial mount (scrollKey starts at 0)

        const timeouts: number[] = [];
        SCROLL_RETRY_DELAYS.forEach((delay) => {
            timeouts.push(window.setTimeout(() => {
                requestAnimationFrame(() => {
                    const table = document.querySelector('.chat-history-table') as HTMLElement;
                    if (table) table.scrollTop = 0;
                    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
                });
            }, delay));
        });
        return () => timeouts.forEach(t => window.clearTimeout(t));
    }, [scrollKey]);

    // Generate table rows with extracted components
    const tableRows = useMemo(() => {
        return currentItems.flatMap((item) => {
            const isUser = item.role.toLowerCase() === "user";
            const isDorc = item.role.toLowerCase() === "dorc";
            const sender = isUser ? "User" : isDorc ? "Dorc" : "AI";

            const timestamp = formatTimestamp(item.created_at);

            let citations = 'NA';
            if (isAIContent(item.content) && item.content.ai_results?.citations) {
                citations = formatCitations(item.content.ai_results.citations);
            }

            const rows: JSX.Element[] = [];

            // User message
            if (sender === "User" && typeof item.content === 'string') {
                rows.push(
                    <UserMessageRow
                        key={`${item._id}-user`}
                        item={item}
                        sender={sender}
                        timestamp={timestamp}
                        citations={citations}
                    />
                );
            }
            // Dorc message
            else if (sender === "Dorc" && isAIContent(item.content)) {
                if (item.content.dorc_results?.length > 0) {
                    const firstDorc = item.content.dorc_results[0];
                    if (firstDorc?.simple_content) {
                        rows.push(
                            <CuratedResponseRow
                                key={`${item._id}-dorc`}
                                item={item}
                                sender={sender}
                                timestamp={timestamp}
                                citations={citations}
                                dorcResult={firstDorc}
                            />
                        );
                    }
                }
            }
            // AI message
            else if (sender === "AI") {
                // Add curated response if available
                if (isAIContent(item.content) &&
                    item.content.dorc_results?.length > 0) {

                    const firstDorc = item.content.dorc_results[0];
                    if (firstDorc?.simple_content) {
                        rows.push(
                            <CuratedResponseRow
                                key={`${item._id}-curated`}
                                item={item}
                                sender={sender}
                                timestamp={timestamp}
                                citations={citations}
                                dorcResult={firstDorc}
                            />
                        );
                    }
                }

                // Add AI response
                const messageContent = extractAIResponseContent(item);
                if (messageContent) {
                    rows.push(
                        <AIMessageRow
                            key={`${item._id}-ai`}
                            item={item}
                            sender={sender}
                            timestamp={timestamp}
                            citations={citations}
                            content={messageContent}
                        />
                    );
                }
            }

            // Fallback for empty rows
            if (rows.length === 0) {
                rows.push(
                    <DefaultMessageRow
                        key={`${item._id}-default`}
                        item={item}
                        sender={sender}
                        timestamp={timestamp}
                        citations={citations}
                    />
                );
            }

            return rows;
        });
    }, [currentItems]);

    return (
        <div className={SK_CHAT_HISTORY_TABLE_CLASS} ref={tableContainerRef}>
            <LoadingIndicator busy={isLoading} />
            <div className="chat-detail-container">
                <Table
                    className={`chat-history-table table-with-sticky-pagination ${!isLoading && currentItems.length === 0 ? 'empty-state' : ''}`}
                    aria-label="Chat detail data"
                    totalCount={totalItems}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
                    setRowsPerPage={handleChangeRowsPerPage}
                    handleFirstPage={handleFirstPage}
                    handlePrevPage={handlePrevPage}
                    handleNextPage={handleNextPage}
                    handleLastPage={handleLastPage}
                    style={{ position: 'relative' }}
                >
                    <TableHeader>
                        <TableRow type="header" role="row">
                            <HeaderField className={DETAIL_TABLE_COLUMN_CLASSES.SENDER} tabIndex={0} role="columnheader" aria-label="Sender">{DETAIL_TABLE_COLUMN_HEADERS.SENDER}</HeaderField>
                            <HeaderField className={DETAIL_TABLE_COLUMN_CLASSES.CONVERSATION} tabIndex={0} role="columnheader" aria-label="Conversation">{DETAIL_TABLE_COLUMN_HEADERS.CONVERSATION}</HeaderField>
                            <HeaderField className={DETAIL_TABLE_COLUMN_CLASSES.TIMESTAMP} tabIndex={0} role="columnheader" aria-label="Timestamp">{DETAIL_TABLE_COLUMN_HEADERS.TIMESTAMP}</HeaderField>
                            <HeaderField className={DETAIL_TABLE_COLUMN_CLASSES.FEEDBACK} tabIndex={0} role="columnheader" aria-label="Feedback">{DETAIL_TABLE_COLUMN_HEADERS.FEEDBACK}</HeaderField>
                            <HeaderField className={DETAIL_TABLE_COLUMN_CLASSES.CITATIONS} tabIndex={0} role="columnheader" aria-label="Citations">
                                {DETAIL_TABLE_COLUMN_HEADERS.CITATIONS}
                                <span
                                    className="citation-info-icon"
                                    role="img"
                                    aria-label="Information about citations"
                                    tabIndex={0}
                                    aria-describedby="citation-tooltip"
                                >
                                    <UpIcon icon="info" />
                                </span>
                                {/* Screen-reader only description, referenced by aria-describedby */}
                                <span id="citation-tooltip" className="sr-only">
                                    Each citation shows the source document and the specific section reference in the AI response.
                                    Format: Document ID [Section ID].
                                </span>
                            </HeaderField>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(!isLoading && currentItems.length === 0) ? (
                            <TableRow className="empty-state-row">
                                <TableField colSpan={5} style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                                    <NoResultsFound
                                        ref={isInitialLoadFailure ? errorMessageRef : null}
                                        message={
                                            isUnauthorizedInitialLoadFailure
                                                ? UNAUTHORIZED_REDIRECT_MESSAGE
                                                : (isInitialLoadFailure ? INITIAL_LOAD_FAILURE_MESSAGE_SESSION_DETAIL : NO_CHAT_SESSION_FOUND)
                                        }
                                        description={
                                            isUnauthorizedInitialLoadFailure
                                                ? UNAUTHORIZED_REDIRECT_DESCRIPTION
                                                : (isInitialLoadFailure ? INITIAL_LOAD_FAILURE_DESCRIPTION : SESSIONS_APPEAR_MESSAGE)
                                        }
                                    />
                                </TableField>
                            </TableRow>
                        ) : (
                            tableRows
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}